import React, { useState } from "react";
import { Box, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import CryptoJS from "crypto-js";
import LoginContainer from "./Component/Container";
import { WORKER_TYPE } from "config/AppConfig";
import AppColor from "utilities/AppColor";
import SignUpLastStep from "./Component/SignUpLastStep";

import {
  reduceSignupWorker,
  reduceSignupGoogleWorker,
  reduceSignupAppleWorker,
  reduceSignupFacebookWorker
} from "store/slices/worker";
import { saveAuthToken } from "store/slices/auth";
import AppDialog from "components/Dialog/AppDialog";
import ComponentLoading from "components/Common/ComponentLoading";
import moment from "moment-timezone";
import { useHistory, useLocation } from "react-router-dom";
import { reduceSignUpCompany, reduceSignupGoogleCompany, reduceSignupFacebookCompany } from "store/slices/company";


const styles = makeStyles((theme) => ({
  loginContainer: {
    width: "calc(100% + 30px)",
    margin: "0 10px",
  },
  paper: {
    padding: "40px 20px 80px 20px",
    borderRadius: 17,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    lineHeight: "38px",
    textAlign: "center",
    marginBottom: '2rem',
    marginTop: '2rem'
  },
  subTitle: {
    fontSize: 15,
    lineHeight: "23px",
    textAlign: "center",
    fontStyle: "italic",
  },
  margin: {
    marginTop: theme.spacing(3),
  },
  button: {
    fontSize: 15,
    fontWeight: "bold",
    borderRadius: 10,
    textTransform: "none",
    "& .MuiButton-label": {
      justifyContent: "space-between",
    },
  },
  success: {
    fontSize: '7rem',
    color: AppColor.mainColor,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    '& .title-success': {
      fontSize: '1rem',
      color: AppColor.black,
      marginTop: '2rem'
    },
    '& .message-success': {
      color: AppColor.black,
      fontSize: '0.8rem',
      marginTop: '0rem'
    }
  }
}));

export default function Signup() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const classes = styles();

  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  
  const companyType = (location.state && location.state.signupType ) ? (location.state.signupType === 'company' ? 'company' : '' ) : '';
  const timezone = moment.tz.guess(true);
  const domain = window.location.hostname;

  const submit = async (data) => {
    setLoading(true);
    let result;
    const params = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      passwordHash: CryptoJS.MD5(data.password).toString(),
      domain: domain,
      timezone: timezone
    }
    
    const isWorkerRole = Object.values(WORKER_TYPE).includes(data.role);
    // const isClientRole = Object.values(CLIENT_TYPE).includes(data.role);

    if (isWorkerRole) {
      params.workerType = data.role;
      result = await dispatch(reduceSignupWorker(params));
    } else if (companyType) {
      result = await dispatch(reduceSignUpCompany(params));
    }

    setLoading(false);
    if (result && result.payload) {
      handleSignupSuccess(result.payload);
    }
  };

  const onAppleResponse = async (data, role) => {
    setLoading(true);
    let result;
    const params = {
      tokenId: data?.authorization?.id_token,
      domain: domain,
      timezone: timezone,
      firstName: data?.user?.name?.firstName,
      lastName: data?.user?.name?.lastName,
    }
    
    const isWorkerRole = Object.values(WORKER_TYPE).includes(role);
    
    if (isWorkerRole) {
      params.workerType = role;
      result = await dispatch(reduceSignupAppleWorker(params));
    }
    setLoading(false);
    if (result && result.payload) {
      handleSignupSuccess(result.payload);
    }
  }

  const onGoogleResponse = async (data, role) => {
    setLoading(true);
    if (data && data.tokenId) {
      let result;
      const params = {
        domain: domain,
        tokenId: data.tokenId,
        timezone: timezone
      }
      
      const isWorkerRole = Object.values(WORKER_TYPE).includes(role);

      if (isWorkerRole) {
        params.workerType = role;
        result = await dispatch(reduceSignupGoogleWorker(params));
      } else if (companyType) {
        result = await dispatch(reduceSignupGoogleCompany(params));
      }
      setLoading(false);
      if (result && result.payload) {
        handleSignupSuccess(result.payload);
      }
    } else {
      setLoading(false);
    }
  };

  const onFacebookResponse = async (data, role) => {
    setLoading(true);
    if (data && data.accessToken && !data.error) {
      let result;
      const params = {
        tokenId: data.accessToken,
        domain: domain,
        timezone: timezone
      }
      params.workerType = role;
      result = await dispatch(reduceSignupFacebookWorker(params));
      
      // const isWorkerRole = Object.values(WORKER_TYPE).includes(role);
      
      // if (isWorkerRole) {
      //   params.workerType = role;
      //   result = await dispatch(reduceSignupFacebookWorker(params));
      // } else if (companyType) {
      //   result = await dispatch(reduceSignupFacebookCompany(params));
      // }
      setLoading(false);
      if (result && result.payload) {
        handleSignupSuccess(result.payload);
        if (window.FB) {
          window.FB.logout(response => console.log(response));
        }
      }
    }
    if ((data && data.status === 'unknown') || (data && data.accessToken && data.error)) {
      setLoading(false);
    }
  };

  const handleSignupSuccess = (data) => {
    setSignupSuccess(true);
    setTimeout(() => {
      dispatch(saveAuthToken({
        accessToken: data.accessToken,
        expireDate: data.expireDate
      }));
    }, 1000);
  };

  const goBack = () => {
    history.goBack();
  }

  return (
    <LoginContainer>
      {
        signupSuccess
          ? (
            <div className={classes.success}>
              <i className="far fa-check-circle"></i>
              <span className='title-success'>{t('text.signupSuccess')}</span>
              <span className='message-success'>{t('text.loggingYouIn')}...</span>
            </div>
          )
          : (
            <React.Fragment>
              <SignUpLastStep
                isSignUpCompany={!!companyType}
                goBackStep1={goBack}
                isSubmitting={loading}
                submit={submit}
                setSignupSuccess={setSignupSuccess}
                onGoogleResponse={onGoogleResponse}
                onFacebookResponse={onFacebookResponse}
                onAppleResponse={onAppleResponse}
              />
              <AppDialog
                open={loading}
                onClose={() => setLoading(false)}
                noTitle
                noCloseButton
                disableEscapeKeyDown
              >
                <Box my={3}>
                  <ComponentLoading />
                </Box>
              </AppDialog>
            </React.Fragment>
          )
      }
    </LoginContainer >
  );
}
